import { observer } from 'mobx-react-lite';
import {
  createContext,
  type RefObject,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ComponentRef,
  type ReactNode,
} from 'react';

import { editorStore } from './utils/editor.store';

interface BaseEditorContextValue {
  floatingContainer: HTMLElement | null;
  editorContainer: HTMLElement | null;
}

const BaseEditorContext = createContext<BaseEditorContextValue | null>(null);

function useBaseEditor() {
  const context = useContext(BaseEditorContext);
  if (!context) {
    throw new Error('useBaseEditort must be used within BaseEditorProvider');
  }
  return context;
}

export function useEditorContainer() {
  const { editorContainer } = useBaseEditor();
  return editorContainer;
}

export function useEditorFloatingContainer() {
  const { floatingContainer } = useBaseEditor();
  return floatingContainer;
}

export interface BaseEditorProviderProps {
  readonly children: ReactNode;
  /**
   * Unique identifier for this editor instance.
   * Used for cursor position persistence.
   */
  editorId: string;
  /**
   * Whether to automatically find the nearest scrollable parent element.
   * When true, searches up the DOM tree for a scrollable container.
   * When false, uses document.body as the container.
   *
   * The scroll container must have `position: relative` or `position: absolute`
   * for floating elements to position correctly.
   *
   * When the container resolves to document.body (this prop is false, or no
   * scrollable ancestor is found), the find bar floats above the editor's top
   * edge instead of overlapping its content. The caller is responsible for
   * leaving clearance above the editor; otherwise the bar can render off-screen
   * when the editor sits near the top of the viewport.
   *
   * @default true
   */
  findScrollableParent: boolean;
}

export function BaseEditorProvider({
  children,
  editorId,
  findScrollableParent,
}: BaseEditorProviderProps) {
  return (
    <BaseEditorProviderInner
      editorId={editorId}
      findScrollableParent={findScrollableParent}
    >
      {children}
    </BaseEditorProviderInner>
  );
}

const BaseEditorProviderInner = observer(function BaseEditorProviderInner({
  children,
  editorId,
  findScrollableParent,
}: BaseEditorProviderProps) {
  const elementRef = useRef<ComponentRef<'div'>>(null);

  useLayoutEffect(() => {
    editorStore.registerEditor(editorId);
    return () => editorStore.unregisterEditor(editorId);
  }, [editorId]);

  const editorContainer = useContainer(elementRef, findScrollableParent);
  const floatingContainer = useFloatingContainer();

  const isReady = editorStore.isEditorActive(editorId);

  return (
    <BaseEditorContext.Provider value={{ floatingContainer, editorContainer }}>
      <div ref={elementRef}>
        {isReady && editorContainer && floatingContainer && children}
      </div>
    </BaseEditorContext.Provider>
  );
});

function useFloatingContainer() {
  const [container, setContainer] = useState<HTMLElement | null>(null);

  useEffect(() => {
    const elem = document.createElement('div');

    elem.classList.add(
      'absolute',
      'top-0',
      'left-0',
      'z-20',
      'pointer-events-none'
    );
    // Tag as editor-owned floating UI so a field's click-away can ignore it.
    elem.setAttribute('data-editor-floating-ui', '');

    document.body.append(elem);

    setContainer(elem);

    return () => elem.remove();
  }, []);

  return container;
}

/**
 * Finds the nearest scrollable ancestor element
 */
function findScrollableParent(element: HTMLElement | null): HTMLElement | null {
  if (!element) return null;

  let parent = element.parentElement;
  while (parent) {
    const { overflow, overflowY } = window.getComputedStyle(parent);
    if (
      overflow === 'auto' ||
      overflow === 'scroll' ||
      overflowY === 'auto' ||
      overflowY === 'scroll'
    ) {
      return parent;
    }
    parent = parent.parentElement;
  }

  return null;
}

function useContainer(
  editorRef: RefObject<ComponentRef<'div'> | null>,
  shouldFindScrollableParent: boolean
) {
  const [parent, setParent] = useState<HTMLElement | null>(
    !shouldFindScrollableParent ? document.body : null
  );

  useEffect(() => {
    if (!shouldFindScrollableParent) return;
    const element = findScrollableParent(editorRef.current);
    setParent(element ?? document.body);
  }, [editorRef, shouldFindScrollableParent]);

  return parent;
}
